// statistics.jsx

import React, { useMemo } from 'react';
import { Box, Typography, styled } from '@mui/material';
import { Timelapse, Schedule, AccessTimeFilled, EventBusy, Logout } from '@mui/icons-material';
import { format } from 'date-fns';
import { cellInfo } from '../utils/timesheetUtils';

const StatBlock = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#ffffff',
  borderRadius: 6, // Reduced 25%
  boxShadow: '0 1.5px 4.5px rgba(0,0,0,0.08)', // Reduced 25%
  padding: theme.spacing(0.75), // Reduced 25%
  gap: theme.spacing(1.5), // Reduced 25%
  flexWrap: 'wrap',
  overflow: 'hidden',
}));

const StatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.4), // Reduced 25%
  padding: theme.spacing(0, 0.75), // Reduced 25%
  borderRight: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderRight: 'none',
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
}));

const Statistics = ({ data, dateRange, cellInfo }) => {
  const stats = useMemo(() => {
    let totalHours = 0;
    let totalIns = 0;
    let totalLate = 0;
    let totalEarly = 0;
    let missingDays = 0;

    data?.members?.forEach((member) => {
      dateRange.forEach((date) => {
        const inf = cellInfo(member, date);
        totalHours += inf.hrs || 0;
        if (inf.hrs > 0) totalIns += 1;
        if (inf.late) totalLate += 1;
        if (inf.earlyLeave) totalEarly += 1;
        const iso = format(date, 'yyyy-MM-dd');
        const recs = member.days.find((d) => d.date === iso)?.records || [];
        if (date.getDay() !== 0 && recs.every((r) => r.event_type !== 'clock_in')) {
          missingDays += 1;
        }
      });
    });

    const latePct = totalIns ? Math.round((totalLate / totalIns) * 100) : 0;
    const earlyPct = totalIns ? Math.round((totalEarly / totalIns) * 100) : 0;

    return {
      totalHours: `${Math.floor(totalHours)}h ${Math.round((totalHours % 1) * 60)}m`,
      totalIns,
      totalLate,
      latePct,
      totalEarly,
      earlyPct,
      missingDays,
    };
  }, [data, dateRange, cellInfo]);

  return (
    <StatBlock>
      {[
        { title: 'Total Hours', value: stats.totalHours, icon: <Timelapse fontSize="small" /> },
        { title: 'Clock-ins', value: stats.totalIns, icon: <Schedule fontSize="small" /> },
        { title: 'Late', value: stats.totalLate, icon: <AccessTimeFilled fontSize="small" /> },
        { title: '% Late', value: `${stats.latePct}%`, icon: <AccessTimeFilled fontSize="small" /> },
        { title: 'Early Leaves', value: stats.totalEarly, icon: <Logout fontSize="small" /> },
        { title: '% Early', value: `${stats.earlyPct}%`, icon: <Logout fontSize="small" /> },
        { title: 'Missing Days', value: stats.missingDays, icon: <EventBusy fontSize="small" /> },
      ].map(({ title, value, icon }) => (
        <StatItem key={title}>
          <IconBox>{icon}</IconBox>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}> {/* Reduced 25% */}
              {title}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 500 }}> {/* Reduced 25% */}
              {value}
            </Typography>
          </Box>
        </StatItem>
      ))}
    </StatBlock>
  );
};

export default Statistics;
